<template>
  <div>
    <NavTop/>
    <NavDark/>

    <section class="space min" aria-label="Kunstnere liste">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-xl-12 col-lg-12 col-md-12 col-sm-12">
            <div class="sec_title position-relative text-center">
              <div class="sec_title position-relative text-center" :data-bgtext="activeCategory?.branche">
                <h1 class="ft-bold pt-3" v-if="activeCategory">{{ activeCategory?.branche }}</h1>
                <h1 class="ft-bold pt-3" v-else>Alle</h1>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div v-if="isLoading && artists.length === 0" class="col-12 text-center py-5" aria-live="polite" aria-busy="true">
            <div class="spinner-border text-dark" role="status" style="width:3rem;height:3rem;">
              <span class="visually-hidden">Indlæser...</span>
            </div>
          </div>

          <div
            v-for="a in artists"
            :key="a.artistId"
            class="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-4"
            role="article"
            :aria-label="a.artistName || 'Kunstner'"
          >
            <div class="card h-100">
              <div class="position-relative" style="overflow:hidden;">
                <RouterLink
                  :to="`/kunstner/${a.artistSubdomain}`"
                  class="d-block"
                  :aria-label="'Se profilside for ' + (a.artistName || 'kunstner')"
                  :title="a.artistName || 'Se kunstnerprofil'"
                  style="display:block; overflow:hidden;"
                >
                  <img
                    :src="a.artistImage"
                    class="card-img-top"
                    :alt="a.artistName ? 'Billede af ' + a.artistName : 'Billede af kunstner'"
                    style="aspect-ratio: 1/1; object-fit: cover; display:block; animation: zoomIn 20s ease-in-out infinite alternate;"
                    loading="lazy"
                    decoding="async"
                  />
                </RouterLink>

                <img
                  v-if="a.artistLogo"
                  :src="a.artistLogo"
                  class="position-absolute bottom-0 end-0 m-2 bg-light p-1 rounded"
                  :alt="a.artistName ? 'Logo for ' + a.artistName : 'Kunstner logo'"
                  style="max-width: 100px; max-height: 50px; object-fit: contain;"
                  loading="lazy"
                  decoding="async"
                />
              </div>



              <div class="card-body d-flex flex-column">
                <div>
                  <h2 class="card-title h5">
                    <RouterLink
                      :to="`/kunstner/${a.artistSubdomain}`"
                      class="text-dark"
                      :aria-label="'Se profilside for ' + (a.artistName || 'kunstner')"
                      :title="a.artistName || 'Kunstner'"
                      v-html="a.artistName"
                    >
                    </RouterLink>
                  </h2>
                  <p class="card-subtitle mb-2 text-muted" v-html="a.artistPersonName + ' · ' + a.artistCity"></p>
                </div>

                <div class="flex-grow-1">
                  <p class="card-text fs-sm" v-html="a.artistShortDescription"></p>
                </div>

                <div class="mt-auto pt-3">
                  <div class="d-flex align-items-center gap-2">
                    <RouterLink
                      :to="`/kunstner/${a.artistSubdomain}`"
                      class="btn btn-sm btn-outline-dark w-100"
                      :aria-label="'Se profilside for ' + (a.artistName || 'kunstner')"
                      :title="'Profilside for ' + (a.artistName || 'kunstner')"
                    >
                      Se profilside
                    </RouterLink>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!isLoading && artists.length === 0" class="col-12 text-center py-5" aria-live="polite">
            <p>Ingen kunstnere fundet.</p>
          </div>
        </div>

        <div class="row" v-if="artists.length && canLoadMore">
          <div class="col-12 text-center">
            <button
              class="btn borders d-inline-flex align-items-center"
              :disabled="isLoading || !canLoadMore"
              @click="loadMore"
              type="button"
              aria-label="Hent flere kunstnere"
            >
              <i class="lni lni-reload me-2" aria-hidden="true"></i>
              Hent flere
            </button>
          </div>
        </div>
      </div>
    </section>

    <FooterTopOne/>
    <FooterDark/>
    <ScrollToTop/>
  </div>
</template>

<script setup>
import NavDark from '@/components/navbar/nav-dark.vue'
import NavTop from '@/components/navbar/nav-top.vue'
import FooterTopOne from '@/components/footer/footer-top-one.vue'
import FooterDark from '@/components/footer/footer-dark.vue'
import ScrollToTop from '@/components/scroll-to-top.vue'

import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useProductStore } from '@/stores/useProductStore'
import { Categories } from '@/data/data'

const store = useProductStore()
const route = useRoute()

const PAGE_LIMIT = 999
const offset = ref(0)

const normalize = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()

const brancheSlug = computed(() => String(route.params.brancheId ?? ''))
const isAllArtists = computed(() => normalize(brancheSlug.value) === 'alle') // NEW

const activeCategory = computed(() => {
  const slug = normalize(brancheSlug.value)
  const list = Array.isArray(Categories) ? Categories : []
  return list.find(c => normalize(c.name) === slug) || null
})

const brancheId = computed(() => activeCategory.value?.id ?? null)

const isLoading = computed(() => store.isLoading)
const artists = computed(() => store.productData)
const canLoadMore = computed(() => store.lastBatchCount === PAGE_LIMIT)

async function fetchArtists({ reset = false } = {}) {
  if (reset) offset.value = 0

  const params = {
    limit: PAGE_LIMIT,
    offset: offset.value,
    sort: 'random',
  }

  if (!isAllArtists.value && brancheId.value) {
    params.branche_id = brancheId.value
  }

  if (!isAllArtists.value && !brancheId.value) {
    store.clearProducts()
    return
  }

  await store.loadProducts({
    mode: 'artists',
    params,
    append: !reset && offset.value > 0,
  })
}

function loadMore() {
  if (!canLoadMore.value || isLoading.value) return
  offset.value += PAGE_LIMIT
  fetchArtists({ reset: false })
}

watch(() => route.params.brancheId, () => fetchArtists({ reset: true }), { immediate: true })
</script>

<style>
@keyframes zoomIn {
  from { transform: scale(1); }
  to   { transform: scale(1.41); }
}
</style>